import { Accordion, AccordionItem } from "@heroui/accordion";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import {
  useAcknowledgeAppraisalMutation,
  useCalculateExpectedScoreMutation,
  useCalculateKraScoreMutation,
  useGetEmployeeByIdQuery,
  useGetMainSelfReviewByAppraisalTypeQuery,
} from "../../../../api/employeeApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import { ActionButtons } from "../../../../global/components/Buttons/buttons";
import bull from "../../../assets/bull.png";
import ApproveModal from "../../HR/Appraisal/Modals/ApproveModal";
import { MeasurementTypeFormatter } from "../../HR/Appraisal/Modals/data";
import { AppraisalDeclineModal } from "../../HR/Appraisal/Modals/DeclineModal";
import AppraisalReviewDetails from "./reviewDetails";

export const LineManager = ({ activeTab }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const { data: employee } = useGetEmployeeByIdQuery(user.staffId);
  const [maxScore, setMaxScore] = useState(0);
  const [openApproveModal, setOpenApproveModal] = React.useState(false);
  const [openDeclineModal, setOpenDeclineModal] = React.useState(false);
  const [appraisalId, setAppraisalId] = useState("");
  const [comment, setComment] = React.useState("");
  const handleComment = (e) => {
    setComment(e.target.value);
  };
  const [onSubmit, { isLoading }] = useAcknowledgeAppraisalMutation();

  const { id } = useParams();
  const { data, error } = useGetMainSelfReviewByAppraisalTypeQuery({
    referenceId: id,
    appraisalType: (activeTab === "HR" && 6) || 0,
    appraiseeId: user.staffId,
  });
  const initialized = React.useRef(false);
  const debounceRef = React.useRef(null);
  const debounceExpected = React.useRef(null);
  const allKras = error
    ? []
    : data?.responseObject?.objectives?.flatMap((obj) => obj.krAs);
  const employeeData = error ? {} : data?.responseObject?.employee;

  const [totalScore, setTotalScore] = useState({
    colorType: "",
    totalAppraisalScore: 0,
    performanceStatusLabelName: "",
  });
  const allObjectives = useMemo(() => {
    return data?.responseObject?.objectives || [];
  }, [data?.responseObject?.objectives]);
  const [onCalculate] = useCalculateKraScoreMutation();
  const [expectedScore] = useCalculateExpectedScoreMutation();
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    if (!allObjectives.length) return; // Early return if no data

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const objectives = allObjectives.map((objective) => {
          const krAs = objective.krAs.map((kra) => {
            return {
              kraId: kra.kraId,
              actualValue:
                kra.employeeReviewDetails?.filter(
                  (item) => item.appraisalType === 1
                )?.[0]?.kraActualScore || 0,
              targetValue: kra.targetValue || 0.01,
              weight: kra.weight,
            };
          });
          return {
            objectiveId: objective.objectiveId,
            objectiveWeight: objective.objectiveWeight,
            krAs,
          };
        });

        const res = await onCalculate({
          employeeId: employeeData?.id || 0,
          referenceId: id,
          objectives,
        }).unwrap();
        if (res) {
          setTotalScore({
            colorType: res.colorType,
            totalAppraisalScore: Math.ceil(res.totalAppraisalScore),
            performanceStatusLabelName: res.performanceStatusLabelName,
          });
        }
      } catch (err) {
        console.error("Error calculating total actual score:", err);
      }
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [employeeData?.id, id, onCalculate, allObjectives]);

  const handleApproveModal = () => setOpenApproveModal(!openApproveModal);
  const handleDeclineModal = () => setOpenDeclineModal(!openDeclineModal);
  const handle360Feedback = async (type) => {
    try {
      const payload = {
        comment,
        appraisalId: appraisalId || 0,
        isOverall: appraisalId ? false : true,
        appraiseeId: user.staffId,
        isRejected: type,
        rejectionComment: comment,
        referenceId: id,
      };
      const response = await onSubmit(payload).unwrap();
      console.log("response", response);
      if (response?.successful) {
        toast.success(response?.statusMessage);
        setOpenApproveModal(false);
        setOpenDeclineModal(false);
      }
      setOpenApproveModal(false);
    } catch (error) {
      toast.error(error?.data?.statusMessage);
    }
  };
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    if (!allObjectives.length) return;
    if (debounceExpected.current) {
      clearTimeout(debounceExpected.current);
    }
    debounceExpected.current = setTimeout(async () => {
      try {
        const objectives = allObjectives.map((objective) => {
          const krAs = objective.krAs.map((kra) => {
            return {
              kraId: kra.kraId,
              actualValue: kra.targetValue || 0,
              targetValue: kra.targetValue || 0.01,
              weight: kra.weight,
            };
          });
          return {
            objectiveId: objective.objectiveId,
            objectiveWeight: objective.objectiveWeight,
            krAs,
          };
        });

        const res = await expectedScore({
          employeeId: employeeData?.id || 0,
          referenceId: id,
          objectives,
        }).unwrap();
        if (res) {
          setMaxScore(res.totalAppraisalScore);
        }
      } catch (err) {
        console.error("Error calculating total actual score:", err);
      }
    }, 500);

    return () => {
      if (debounceExpected.current) {
        clearTimeout(debounceExpected.current);
      }
    };
  }, [employeeData?.id, id, expectedScore, allObjectives]);
  return (
    <div>
      <div className="flex items-center justify-between my-4">
        <div className="flex items-center space-x-3">
          <div>
            <p className="text-[#00AB09]">
              Total Score: {totalScore.totalAppraisalScore}/{maxScore} (
              {totalScore.totalAppraisalScore / maxScore * 100})%
            </p>
          </div>
        </div>
      </div>
      <div>
        {allKras?.map((items, kraIndex) => (
          <Accordion
            key={kraIndex + 1}
            aria-label={`Accordion ${kraIndex + 1}`}
            selectionMode="single"
            defaultExpandedKeys={["1"]}
            titleWrapper="shadow-md rounded-md border border-gray-200"
          >
            <AccordionItem
              key={kraIndex + 1}
              title={
                <div className="flex items-center gap-5 px-5">
                  <img src={bull} alt="" />
                  <span className="text-[#4B664C] font-[600]">
                    KRA: {items?.kraTitle}
                  </span>
                </div>
              }
              className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
            >
              <div>
                <div className="flex items-center justify-between px-5">
                  <div />
                  <div>
                    <p className="text-[#4B664C] font-[600]">
                      isAutogenerated: {items.isAutoGenerated ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
                <div
                  key={kraIndex + 1}
                  className="flex items-center gap-5 py-2 px-5 border-b mb-4"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "180px 1fr",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <h2 className="text-[#305F32] font-[600]">
                    Title: {items.kraTitle}
                  </h2>
                  <h4 className="text-[#305F32] font-[500]">
                    Description: {items.kraDescription}
                  </h4>
                  <h2 className="text-[#305F32] font-[600]">
                    Target: {items.kraTarget}
                  </h2>
                  <h4 className="text-[#00AB09] font-[500]">
                    Score:{" "}
                    {items?.employeeReviewDetails?.filter(
                      (item) => item.appraisalType === 1
                    )?.[0]?.kraActualScore || "Not scored yet"}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    Measurement Type:{" "}
                    {MeasurementTypeFormatter(items.measurementType)}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    Source: {items.source}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    KRA Type: {items.kraType}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    Include Self Review:{" "}
                    {items.includeSelfReview ? "Yes" : "No"}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    Period Start Date:{" "}
                    {FormatDateTime(items.periodStartDate, "DD/MM/YYYY")}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    Period End Date:{" "}
                    {FormatDateTime(items.periodEndDate, "DD/MM/YYYY")}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    Next Due Date:{" "}
                    {FormatDateTime(items.nextDueDate, "DD/MM/YYYY")}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    SLA Due Date:{" "}
                    {FormatDateTime(items.slaDueDate, "DD/MM/YYYY")}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    SLA Reminder Count: {items.slaReminderCount}
                  </h4>
                  <h4 className="text-[#305F32] font-[500]">
                    SLA Start Duration: {items.slaStartDuration}
                  </h4>
                </div>
                <div className="px-5"></div>
                {(activeTab === "Line Manager"
                  ? items?.employeeReviewDetails
                  : items?.employeeReviewDetails?.filter(
                      (dat) =>
                        dat?.appraisalType === 1 ||
                        dat?.appraisalType === 2 ||
                        dat?.appraisalType === 3 ||
                        dat?.appraisalType === 4
                    )
                )?.map((item) => (
                  <div>
                    <div className="bg-[#f9fbf7] rounded-xl border border-gray-200 p-6 w-full text-base">
                      <AppraisalReviewDetails
                        item={item}
                        showDocument={
                          item?.appraisalType === 1 ||
                          item?.appraisalType === 2 ||
                          item?.appraisalType === 3 ||
                          item?.appraisalType === 4
                        }
                        peerReviewAffectsScore={items?.peerReviewAffectsScore}
                        upwardReviewAffectsScore={
                          items?.upwardReviewAffectsScore
                        }
                        crossReviewAffectsScore={items?.crossReviewAffectsScore}
                      />

                      {activeTab === "Line Manager" && (item?.appraisalType > 1) && (item?.appraisalType < 5) && (
                        <div className="px-5 mt-4">
                          <div className={DashboardStyle.button_cage}>
                            <button
                              width={"auto"}
                              disabled={item?.isAcknowledged}
                              className={DashboardStyle?.button_cage_weight}
                              onClick={() => {
                                setAppraisalId(item.id);
                                handleApproveModal();
                              }}
                              type="button"
                            >
                              {item?.isAcknowledged
                                ? "Accepted"
                                : "Accept Review"}
                            </button>
                            <ActionButtons
                              width={"auto"}
                              disabled={item?.isAcknowledged === false}
                              className={DashboardStyle?.button_cage_weight}
                              style={{ backgroundColor: "red" }}
                              onClick={() => {
                                setAppraisalId(item.id);
                                handleDeclineModal();
                              }}
                              type="button"
                            >
                              {item?.isAcknowledged === false
                                ? "Rejected"
                                : "Reject review"}
                            </ActionButtons>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </AccordionItem>
          </Accordion>
        ))}
      </div>

      {openDeclineModal && (
        <AppraisalDeclineModal
          openModal={openDeclineModal}
          closeModal={handleDeclineModal}
          handleComment={handleComment}
          handleDelete={() => handle360Feedback(true)}
          isLoading={isLoading}
          title="Reason For Rejection"
        />
      )}
      <ApproveModal
        isOpen={openApproveModal}
        handleClose={handleApproveModal}
        handleDelete={() => handle360Feedback(false)}
        handleComment={handleComment}
        isLoading={isLoading}
        headText={"Accept review"}
      />

      {activeTab === "Line Manager" && (
        <div className="px-5">
          <div className={DashboardStyle.button_cage}>
            <button
              width={"auto"}
              className={DashboardStyle?.button_cage_weight}
              onClick={() => {
                setAppraisalId("");
                handleApproveModal();
              }}
              type="button"
            >
              Accept Review
            </button>
            <ActionButtons
              width={"auto"}
              className={DashboardStyle?.button_cage_weight}
              style={{ backgroundColor: "red" }}
              onClick={() => {
                setAppraisalId("");
                handleDeclineModal();
              }}
              type="button"
            >
              Reject review
            </ActionButtons>
          </div>
        </div>
      )}
    </div>
  );
};
